import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { useUser } from '~settings/api/queries/user';
import { ContentBox } from '~settings/components/ContentBox';
import { SETTINGS_ROUTES } from '~settings/config/routes';

import { UserDetailProvider } from './contexts/UserDetailProvider';
import { UserDetailsBlock } from './parts/UserDetailsBlock';
import { UserHeader } from './parts/UserHeader';
import { UserLabels } from './parts/UserLabels';

type UserDetailsRouteParams = {
  userId: string;
};

export const DetailUserPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const { userId } = useParams<UserDetailsRouteParams>();
  const { isLoading, error, user } = useUser(userId || '');

  useEffect(() => {
    if (!userId) {
      navigate(SETTINGS_ROUTES.ROOT);
    }
  }, [userId, navigate]);

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !user) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.ROOT,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <UserDetailProvider user={user}>
      <UserHeader />
      <article className="flex gap-md py-md">
        <section className="inline-flex min-w-[19rem] flex-none basis-1/5 flex-col gap-md text-body-sm">
          <UserDetailsBlock />
        </section>
        <ContentBox className="flex-1 rounded-sm py-lg">
          <UserLabels />
        </ContentBox>
      </article>
    </UserDetailProvider>
  );
};

DetailUserPage.displayName = 'DetailUserPage';
